import {
  Component, OnInit, ViewEncapsulation, Input, ChangeDetectionStrategy, Output, EventEmitter, ChangeDetectorRef,
} from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';

import { isNullOrUndefined } from '@app/util/util';
import { FilterApplier, FilterTypes } from '@shared/flight-itinerary/entities/filters';
import { CheckboxFilterVm, CheckboxFilter } from '@shared/filters/entities/fetch-filters.response';
import { MULTIPLE_CARRIERS, AIRLINES_PRICES_TITLES } from '@flight-results/constants/filters.constants';
import { HotelFilterTypes } from '@shared/hotel-itinerary/hotel-filters';
import { NO_ZONE_LABEL, HOTEL_NON_REFUNDABLE_TITLES } from '@hotels/hotel-results/constants/filters';
import { hotelFilterIsNoZoneLabel } from '@app/mappers/global-filters/hotel-global-filters.mapper';
import { CheckboxAlign } from '@design-system/components/organisms/checkbox/entities/checkbox.types';
import { ActivitiesFilterTypes } from '@activities/activities-results/entities/activities-filters';
import { getActivityTypeLabelKey } from '@activities/mappers/activities-type.mapper';
import {
  TypographyFontFamily, TypographyFontSize, TypographyColor
} from '@design-system/components/atoms/typography/entities/typography.types';

@Component({
  selector: 'app-checkbox-filter',
  templateUrl: './checkbox-filter.component.html',
  styleUrls: ['./checkbox-filter.component.sass'],
  encapsulation: ViewEncapsulation.ShadowDom,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class CheckboxFilterComponent implements OnInit {

  @Input() label: string;
  @Input() type: string;
  @Input() options: CheckboxFilterVm[] | CheckboxFilter[] = [];
  @Input() index = -1;
  @Input() isMobile = false;
  @Input() pickedCheckboxes: string[] = [];
  @Input() isOpenBox: boolean;
  @Input() noTranslate = false;
  @Input() individualFilter = false;

  @Output() filter: EventEmitter<FilterApplier> = new EventEmitter<FilterApplier>();

  public seeMore = false;
  public activeDisabled = false;

  public readonly fontFamily = TypographyFontFamily;
  public readonly fontSize = TypographyFontSize;
  public readonly fontColor = TypographyColor;

  private readonly maxViewOptions = 5;

  constructor(
    private readonly changeDetector: ChangeDetectorRef,
    private readonly translateService: TranslocoService
  ) { }

  ngOnInit(): void {
    if (!this.isOpenBox) {
      this.isOpenBox = this.index === 0;
    }
    this.activeDisabledDefault();
    this.detectChanges();
  }

  get checkboxAlign(): CheckboxAlign {
    return this.isMobile ? CheckboxAlign.Right : CheckboxAlign.Left;
  }

  get withTranslations(): boolean {
    return this.type === HotelFilterTypes.NonRefundable;
  }

  get seeMoreActive(): boolean {
    return this.options && this.options.length > this.maxViewOptions;
  }

  get isStops(): boolean {
    return this.type === FilterTypes.Stops;
  }
  
  get isActivityTypes(): boolean {
    return this.type === ActivitiesFilterTypes.ActivityType;
  }

  get carrierLabel(): string {
    return AIRLINES_PRICES_TITLES[MULTIPLE_CARRIERS];
  }

  get isFullView(): boolean {
    return !this.seeMoreActive || this.seeMore;
  }

  get isExpandedView(): boolean {
    return this.individualFilter && this.seeMore;
  }

  private get isUniqueSelection(): boolean {
    return this.type === FilterTypes.AirPriceRanges ||
      this.type === HotelFilterTypes.NonRefundable;
  }

  public getActivityTypeLabel(item: string): string {
    return getActivityTypeLabelKey(item);
  }

  public getRatingTagLabel(item: string): string {
    if (this.type === HotelFilterTypes.Score) {
      const numericItem = parseFloat(item);
      return !isNaN(numericItem) ? 
        this.translateService.translate('HOTELS_MODULE.FILTER_HOTELS.TAG_SCORES', 
          { score: numericItem.toFixed(1) }) : null;
    }
    return null;
  }

  public isNoZoneLabel(item: CheckboxFilter): boolean {
    return hotelFilterIsNoZoneLabel(item);
  }

  public checked(item: CheckboxFilterVm): boolean {
    return this.pickedCheckboxes.includes(item.Text);
  }

  public isMultipleCarriers(item: CheckboxFilterVm): boolean {
    return item.Text === MULTIPLE_CARRIERS;
  }

  public toggleCheckboxOptions(): void {
    this.isOpenBox = !this.isOpenBox;
  }

  public select(option: CheckboxFilterVm): void {
    const removeItem = this.pickedCheckboxes.find(item => item === option.Text);
    const removable = !isNullOrUndefined(removeItem);
    this.filter.emit({ type: this.type as FilterTypes, value: option.Text, removable });
    this.activeDisabled = this.isUniqueSelection && !removable;
  }

  public toggleMoreFliters(): void {
    this.seeMore = !this.seeMore;
  }

  public isDisabled(option: CheckboxFilterVm): boolean {
    if (this.pickedCheckboxes && this.pickedCheckboxes.length === 0) {
      return false;
    }
    const selection = this.pickedCheckboxes.find(checkbox => checkbox === option.Text);
    return this.activeDisabled && isNullOrUndefined(selection);
  }

  public getCheckboxLabel(item: CheckboxFilterVm): string | number {
    if (this.withTranslations) {
      return this.labelTranslation(item);
    } else if (item.TextTransformed) {
      return item.TextTransformed;
    } else if (this.isNoZoneLabel(item)) {
      return this.translateService.translate(NO_ZONE_LABEL);
    } else if (item.Label) {
      return item.Label.toString();
    } else {
      return item.Text;
    }
  }

  private labelTranslation(item: CheckboxFilter): string {
    return this.type === HotelFilterTypes.NonRefundable ?
      this.translateService.translate(HOTEL_NON_REFUNDABLE_TITLES[item.Text]) : item.Text;
  }

  private detectChanges(): void {
    this.changeDetector.detectChanges();
  }

  private activeDisabledDefault(): void {
    const localSelection = this.pickedCheckboxes.filter(picked =>
      this.options.some((option: CheckboxFilterVm) => picked === option.Text));
    this.activeDisabled = !!this.isUniqueSelection && !!localSelection?.length;
  }

}
